'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef, useState } from 'react';
import { useClerk } from '@clerk/nextjs';
import { Button } from '@components/ui/button';
import { useGoogleSignIn } from '@lib/auth/hooks/use-google-signin';
import { collectAuthDiagnostics } from '@lib/clerk/utils/collect-auth-diagnostics';
import { resetSessionAndReload } from '@lib/clerk/utils/reset-session-and-reload';
import { getLogger } from '@lib/observability/logging/log.client';
import { ErrorIndicator } from '@/components/ui/error-indicator';
const {
  logError
} = getLogger('app/(public)/(auth)/_components/google-sign-in-button');

// A working SSO hard-navigates to Google; `pagehide` fires only when
// the browser commits the new document, which on a slow network can
// lag several seconds behind the redirect. The window is generous: a
// dead button never navigates, so erring long only delays the rare
// recovery, while erring short risks tripping a slow-but-working
// redirect.
const NAV_WATCHDOG_MS = 10000;

// Caps auto-reset at one per tab session, so a failure the reset
// doesn't cure falls back to a manual retry instead of looping.
const AUTO_RESET_FLAG = 'clai:google-signin-auto-reset';
function autoResetAlreadyTried(): boolean {
  try {
    return window.sessionStorage.getItem(AUTO_RESET_FLAG) === '1';
  } catch {
    // Storage blocked (privacy mode): treat as not-yet-tried.
    return false;
  }
}
function markAutoResetTried(): void {
  try {
    window.sessionStorage.setItem(AUTO_RESET_FLAG, '1');
  } catch {
    // Storage blocked: the worst case is one extra reset, not a loop
    // — without persistence each click is its own first attempt.
  }
}
interface GoogleSignInButtonProps {
  children: React.ReactNode;
  className?: string;
  redirectedFrom?: string | null;
  /**
   * Where the attempt lands when it creates the account.
   * Omit to leave new accounts on Clerk's sign-up fallback.
   */
  signUpRedirectUrl?: string;
  onStart?: () => void;
  /**
   * Fires when the redirect won't happen after `onStart` — a failed
   * SSO or the no-nav watchdog. Lets a parent that flipped into a
   * "redirecting" state on `onStart` reverse it instead of staying
   * stuck on a skeleton.
   */
  onRedirectAborted?: () => void;
  disabled?: boolean;
}
export function GoogleSignInButton(t0) {
  const $ = _c(27);
  const {
    children,
    className,
    redirectedFrom,
    signUpRedirectUrl,
    onStart,
    onRedirectAborted,
    disabled
  } = t0;
  const [isLoading, setIsLoading] = useState(false);
  const [isStuck, setIsStuck] = useState(false);
  const {
    signInWithGoogle,
    error
  } = useGoogleSignIn();
  const clerk = useClerk();
  const navigatedRef = useRef(false);
  const watchdogRef = useRef(null);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      const onPageHide = () => {
        navigatedRef.current = true;
      };
      window.addEventListener("pagehide", onPageHide);
      return () => {
        window.removeEventListener("pagehide", onPageHide);
        if (watchdogRef.current !== null) {
          window.clearTimeout(watchdogRef.current);
        }
      };
    };
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useEffect(t1, t2);
  let t3;
  if ($[2] !== clerk || $[3] !== onRedirectAborted || $[4] !== onStart || $[5] !== redirectedFrom || $[6] !== signInWithGoogle || $[7] !== signUpRedirectUrl) {
    t3 = async () => {
      onStart?.();
      setIsStuck(false);
      navigatedRef.current = false;
      setIsLoading(true);
      ;
      try {
        await signInWithGoogle(redirectedFrom, signUpRedirectUrl);
      } catch (t4) {
        const err = t4;
        logError("Google sign-in error", err as Error);
        setIsLoading(false);
        onRedirectAborted?.();
        return;
      }
      if (watchdogRef.current !== null) {
        window.clearTimeout(watchdogRef.current);
      }
      watchdogRef.current = window.setTimeout(() => {
        watchdogRef.current = null;
        if (navigatedRef.current) {
          return;
        }
        logError("Google sign-in: SSO accepted but no navigation", undefined, collectAuthDiagnostics());
        if (autoResetAlreadyTried()) {
          setIsLoading(false);
          setIsStuck(true);
          onRedirectAborted?.();
          return;
        }
        markAutoResetTried();
        resetSessionAndReload(clerk);
      }, NAV_WATCHDOG_MS);
    };
    $[2] = clerk;
    $[3] = onRedirectAborted;
    $[4] = onStart;
    $[5] = redirectedFrom;
    $[6] = signInWithGoogle;
    $[7] = signUpRedirectUrl;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const handleGoogleSignIn = t3;
  const t4 = `flex w-full items-center justify-center gap-2 disabled:border-solid disabled:shadow-sm ${className || ""}`;
  let t5;
  if ($[9] !== isLoading) {
    t5 = !isLoading && <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" /><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" /><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" /><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" /></svg>;
    $[9] = isLoading;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== children || $[12] !== disabled || $[13] !== handleGoogleSignIn || $[14] !== isLoading || $[15] !== t4 || $[16] !== t5) {
    t6 = <Button type="button" variant="outline" onClick={handleGoogleSignIn} loading={isLoading} disabled={disabled} className={t4} data-sentry-element="Button" data-sentry-source-file="google-sign-in-button.tsx">{t5}{children}</Button>;
    $[11] = children;
    $[12] = disabled;
    $[13] = handleGoogleSignIn;
    $[14] = isLoading;
    $[15] = t4;
    $[16] = t5;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== error) {
    t7 = error && <ErrorIndicator message={error.message} variant="compact" />;
    $[18] = error;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== clerk || $[21] !== isStuck) {
    t8 = isStuck && <div className="flex flex-col gap-2"><ErrorIndicator message="Sign-in still didn't go through. Reset and try again." variant="compact" /><Button type="button" variant="outline" onClick={() => resetSessionAndReload(clerk)} className="w-full">{"Reset & retry"}</Button></div>;
    $[20] = clerk;
    $[21] = isStuck;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] !== t6 || $[24] !== t7 || $[25] !== t8) {
    t9 = <div className="flex w-full flex-col gap-2" data-sentry-component="GoogleSignInButton" data-sentry-source-file="google-sign-in-button.tsx">{t6}{t7}{t8}</div>;
    $[23] = t6;
    $[24] = t7;
    $[25] = t8;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  return t9;
}